import { app } from '../../app';
import { CollideEvent, CollideId } from '../../defs/collide';
import type { PantherBlockEntity } from '../../entities/BlockEntity';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { hasBlockBelow, mapGetPan } from '../../util/mapTools';
import { InvalidEffect } from '../effects/InvalidEffect';
import { PantherBlockEffect } from '../effects/PantherBlockEffect';
import { CollideHandler, ICollision } from './ICollision';

/*
    panther block collision resolver
*/
export class PantherCollision implements ICollision {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: PantherBlockEntity;
  // map
  private readonly _resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this._resolveTap,
    fall: this._resolveFall,
  };

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, entity: PantherBlockEntity) {
    this._scene = scene;
    this._entity = entity;
  }

  // impl
  //-------------------------------------------------------------------------
  public async resolve(event: CollideEvent): Promise<boolean> {
    return this._resolvers[event.id]?.call(this, event) ?? false;
  }

  // private: resolvers
  //-------------------------------------------------------------------------
  private async _resolveTap(event: CollideEvent) {
    // invalid effect
    await new InvalidEffect(this._entity).execute();
    return false;
  }

  private async _resolveFall(event: CollideEvent) {
    // if does not have block below
    if (!this._findBlockBelow()) {
      // destroy
      await this._actionDestroy();
      return true;
    }
    return false;
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _actionDestroy() {
    const position = this._entity.c.position.mapPosition;

    // play sound
    app.sound.play('puzzle-panther-destroy', {
      pan: mapGetPan(position),
    });

    const effect = new PantherBlockEffect(this._scene, {
      entity: this._entity,
    });
    effect.animateMilkParticles();
    effect.animateBreakParticles();

    // despawn block
    despawnBlockEntity(this._scene, this._entity);
  }

  // private: support
  //-------------------------------------------------------------------------
  private _findBlockBelow(): boolean {
    return hasBlockBelow(this._scene.sessionEntity.c.map, this._entity);
  }
}
